import { useEffect, useMemo, useState } from 'react';
import {
  IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonList,
  IonMenuButton, IonNote, IonPage, IonSelect, IonSelectOption, IonSpinner, IonTextarea, IonTitle,
  IonToolbar,
} from '@ionic/react';
import { checkmarkCircleOutline, refreshOutline, sparklesOutline, warningOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { PLATFORM_IDS, type PlatformId } from '@ai-media/shared';
import {
  assistantApi, apiErrorMessage, RANGE_OPTIONS, TONES,
  type AssistantAnalytics, type AssistantNarrative, type AssistantRange, type CampaignPlanInput,
  type CampaignProposal, type Capabilities, type RankedPost, type Tone, type VariationResult,
} from '@/lib/assistant';
import { asArray, asNumber } from '@/lib/collections';
import { toast } from '@/store/ui.store';
import PlatformChip from '@/components/PlatformChip';
import EmptyState from '@/components/EmptyState';

type Outcome =
  | { kind: 'variations'; variants: VariationResult[]; degraded: boolean }
  | { kind: 'proposal'; proposal: CampaignProposal; plan: CampaignPlanInput; creating: boolean }
  | { kind: 'narrative'; narrative: AssistantNarrative }
  | { kind: 'summary'; analytics: AssistantAnalytics }
  | { kind: 'top'; posts: RankedPost[]; metric: string };

const numberFormat = new Intl.NumberFormat('en');

/**
 * The in-product twin of the MCP server: the same `/assistant/*` endpoints, with
 * the same confirm-before-write rule — a campaign is only persisted after the
 * user presses "Approve & create".
 */
export default function AssistantPage() {
  const { t } = useTranslation();
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);
  const [prompt, setPrompt] = useState('');
  const [platforms, setPlatforms] = useState<PlatformId[]>(['instagram']);
  const [tone, setTone] = useState<Tone>('friendly');
  const [range, setRange] = useState<AssistantRange>('last_7_days');
  const [busy, setBusy] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const loadCapabilities = async () => {
    try {
      setCapabilities(await assistantApi.capabilities());
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  };

  useEffect(() => {
    void loadCapabilities();
  }, []);

  const connected = useMemo(
    () => capabilities?.platforms?.filter((platform) => platform.connected).map((platform) => platform.id) ?? [],
    [capabilities]
  );

  const run = async (action: string, task: () => Promise<void>) => {
    setBusy(action);
    setOutcome(null);
    try {
      await task();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setBusy(null);
    }
  };

  const generateVariations = () =>
    run('variations', async () => {
      if (prompt.trim().length < 3) {
        toast(t('assistant.needTopic'), 'warning');
        return;
      }
      const data = await assistantApi.variations({ topic: prompt.trim(), platforms, tone });
      setOutcome({ kind: 'variations', variants: data.variants, degraded: false });
    });

  const planCampaign = () =>
    run('proposal', async () => {
      const name = prompt.trim() || t('assistant.defaultCampaignName');
      const plan: CampaignPlanInput = { name, platforms, durationDays: 14, postsPerWeek: 3, tone };
      const proposal = await assistantApi.planCampaign(plan);
      setOutcome({ kind: 'proposal', proposal, plan, creating: false });
    });

  const confirmCampaign = () =>
    run('confirm', async () => {
      if (outcome?.kind !== 'proposal') return;
      setOutcome({ ...outcome, creating: true });
      try {
        const created = await assistantApi.createCampaign({ ...outcome.plan, confirm: true });
        toast(
          created.demo ? t('assistant.campaignCreatedDemo') : t('assistant.campaignCreated', { name: created.campaign.name }),
          'success'
        );
        setOutcome(null);
        setPrompt('');
      } catch (error) {
        toast(apiErrorMessage(error), 'danger');
        setOutcome({ ...outcome, creating: false });
      }
    });

  const summarize = () =>
    run('narrative', async () => {
      setOutcome({ kind: 'narrative', narrative: await assistantApi.summarize({ range }) });
    });

  const loadSummary = () =>
    run('summary', async () => {
      setOutcome({ kind: 'summary', analytics: await assistantApi.analyticsSummary({ range }) });
    });

  const loadTopPosts = () =>
    run('top', async () => {
      const data = await assistantApi.topPosts({ range, limit: 5 });
      setOutcome({ kind: 'top', posts: data.posts, metric: data.metric });
    });

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
            <IonBackButton defaultHref="/app" />
          </IonButtons>
          <IonTitle>{t('assistant.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void loadCapabilities()} disabled={busy !== null}>
              <IonIcon slot="icon-only" icon={refreshOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="page-shell">
          {capabilities === null ? (
            <div className="empty-state"><IonSpinner /></div>
          ) : (
            <>
              <div className="dash-row" style={{ flexWrap: 'wrap', gap: '8px' }}>
                {capabilities.demoMode && (
                  <span className="status-pill status-pill--warning">
                    <IonIcon icon={warningOutline} /> {t('assistant.demoMode')}
                  </span>
                )}
                {capabilities.brandVoice?.configured && (
                  <span className="status-pill">{t('assistant.brandConfigured', { tone: capabilities.brandVoice?.tone })}</span>
                )}
                {connected.length > 0 ? (
                  connected.map((id) => <PlatformChip key={id} platform={id} />)
                ) : (
                  <IonNote>{t('assistant.noAccounts')}</IonNote>
                )}
              </div>

              <IonItem>
                <IonLabel position="stacked">{t('assistant.prompt')}</IonLabel>
                <IonTextarea
                  autoGrow
                  value={prompt}
                  onIonInput={(event) => setPrompt((event.target as HTMLIonTextareaElement).value ?? '')}
                  placeholder={t('assistant.promptPlaceholder')}
                  aria-label={t('assistant.prompt')}
                />
              </IonItem>

              <div className="dash-row" style={{ flexWrap: 'wrap', gap: '8px' }}>
                {PLATFORM_IDS.map((id) => (
                  <IonButton
                    key={id}
                    size="small"
                    fill={platforms.includes(id) ? 'solid' : 'outline'}
                    color={platforms.includes(id) ? 'primary' : 'medium'}
                    onClick={() =>
                      setPlatforms((current) =>
                        current.includes(id) ? current.filter((p) => p !== id) : [...current, id]
                      )
                    }
                  >
                    {t(`platforms.${id}`)}
                  </IonButton>
                ))}
              </div>

              <div className="dash-row" style={{ flexWrap: 'wrap', gap: '8px' }}>
                <IonSelect
                  interface="popover"
                  label={t('assistant.tone')}
                  value={tone}
                  onIonChange={(event) => setTone((event.target as HTMLIonSelectElement).value as Tone)}
                >
                  {TONES.map((option) => (
                    <IonSelectOption key={option} value={option}>{option}</IonSelectOption>
                  ))}
                </IonSelect>
                <IonSelect
                  interface="popover"
                  label={t('assistant.range')}
                  value={range}
                  onIonChange={(event) => setRange((event.target as HTMLIonSelectElement).value as AssistantRange)}
                >
                  {RANGE_OPTIONS.map((option) => (
                    <IonSelectOption key={option} value={option}>{option.replace(/_/g, ' ')}</IonSelectOption>
                  ))}
                </IonSelect>
              </div>

              <div className="dash-row" style={{ flexWrap: 'wrap', gap: '8px' }}>
                <IonButton expand="block" onClick={generateVariations} disabled={busy !== null || platforms.length === 0}>
                  <IonIcon slot="start" icon={sparklesOutline} />
                  {t('assistant.generateVariations')}
                </IonButton>
                <IonButton expand="block" fill="outline" onClick={planCampaign} disabled={busy !== null || platforms.length === 0}>
                  {t('assistant.planCampaign')}
                </IonButton>
              </div>

              <h6 className="section-title">{t('assistant.analytics')}</h6>
              <div className="dash-row" style={{ flexWrap: 'wrap', gap: '8px' }}>
                <IonButton size="small" fill="outline" onClick={loadSummary} disabled={busy !== null}>
                  {t('assistant.summary')}
                </IonButton>
                <IonButton size="small" fill="outline" onClick={loadTopPosts} disabled={busy !== null}>
                  {t('assistant.topPosts')}
                </IonButton>
                <IonButton size="small" fill="outline" onClick={summarize} disabled={busy !== null}>
                  {t('assistant.narrative')}
                </IonButton>
              </div>

              {busy && <div className="empty-state"><IonSpinner /></div>}
              {!busy && outcome === null && (
                <EmptyState icon={sparklesOutline} title={t('assistant.emptyTitle')} body={t('assistant.emptyMessage')} />
              )}

              {!busy && outcome?.kind === 'variations' && (
                <IonList inset>
                  {asArray(outcome.variants).map((variant) => (
                    <IonItem key={variant.platformId}>
                      <IonLabel className="ion-text-wrap">
                        <div className="dash-row">
                          <PlatformChip platform={variant.platformId} />
                          <IonNote>{asNumber(variant.charCount)} chars · {asNumber(variant.hashtagCount)} hashtags</IonNote>
                        </div>
                        <p>{variant.caption}</p>
                        {asArray(variant.errors).map((error) => (
                          <IonNote key={error} color="danger"><IonIcon icon={warningOutline} /> {error}</IonNote>
                        ))}
                        {asArray(variant.warnings).map((warning) => (
                          <IonNote key={warning} color="warning">{warning}</IonNote>
                        ))}
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              )}

              {!busy && outcome?.kind === 'proposal' && (
                <IonList inset>
                  <IonItem>
                    <IonLabel className="ion-text-wrap">
                      <h3>{outcome.proposal.name}</h3>
                      <p>{outcome.proposal.summary}</p>
                      <IonNote>
                        {t('assistant.proposalMeta', {
                          items: outcome.proposal.itemTotal,
                          days: outcome.proposal.durationDays,
                          cadence: outcome.proposal.cadence,
                        })}
                      </IonNote>
                      {outcome.proposal.mode === 'demo' && <IonNote color="warning">{t('assistant.demoProposal')}</IonNote>}
                    </IonLabel>
                  </IonItem>
                  {asArray(outcome.proposal.items).slice(0, 5).map((item) => (
                    <IonItem key={`${item.publishAt}-${item.title}`}>
                      <IonLabel className="ion-text-wrap">
                        <div className="dash-row">
                          {asArray(item.platformIds).map((id) => <PlatformChip key={id} platform={id} />)}
                          <IonNote>{new Date(item.publishAt).toLocaleString()}</IonNote>
                        </div>
                        <h4>{item.title}</h4>
                        <p>{item.hook}</p>
                        {!item.valid && <IonNote color="danger">{asArray(item.warnings).join(', ')}</IonNote>}
                      </IonLabel>
                    </IonItem>
                  ))}
                  <IonItem>
                    <IonLabel className="ion-text-wrap">
                      <IonNote>{t('assistant.confirmHint')}</IonNote>
                    </IonLabel>
                    <IonButton slot="end" onClick={confirmCampaign} disabled={busy !== null}>
                      <IonIcon slot="start" icon={checkmarkCircleOutline} />
                      {t('assistant.approveCreate')}
                    </IonButton>
                  </IonItem>
                </IonList>
              )}


              {!busy && outcome?.kind === 'narrative' && (
                <IonList inset>
                  <IonItem>
                    <IonLabel className="ion-text-wrap">
                      <h3>{outcome.narrative.summary}</h3>
                      {asArray(outcome.narrative.insights).map((insight) => (
                        <p key={insight}>• {insight}</p>
                      ))}
                      <IonNote>
                        {t('assistant.narrativeMeta', {
                          posts: asNumber(outcome.narrative.postsPublished),
                          rate: asNumber(outcome.narrative.engagementRate).toFixed(2),
                          model: outcome.narrative.model,
                        })}
                      </IonNote>
                      {outcome.narrative.degraded && <IonNote color="warning">{t('assistant.degraded')}</IonNote>}
                    </IonLabel>
                  </IonItem>
                </IonList>
              )}

              {!busy && outcome?.kind === 'summary' && (
                <IonList inset>
                  <IonItem>
                    <IonLabel className="ion-text-wrap">
                      <h3>{outcome.analytics.headline}</h3>
                      {asArray(outcome.analytics.highlights).map((highlight) => (
                        <p key={highlight}>• {highlight}</p>
                      ))}
                      <IonNote>
                        {t('assistant.rangeMeta', {
                          label: outcome.analytics.range?.label,
                          posts: asNumber(outcome.analytics.postsPublished),
                          rate: asNumber(outcome.analytics.engagementRate).toFixed(2),
                        })}
                      </IonNote>
                      {asArray(outcome.analytics.flags).map((flag) => (
                        <IonNote key={flag} color="warning"><IonIcon icon={warningOutline} /> {flag}</IonNote>
                      ))}
                    </IonLabel>
                  </IonItem>
                </IonList>
              )}

              {!busy && outcome?.kind === 'top' && (
                <IonList inset>
                  {asArray(outcome.posts).length === 0 ? (
                    <IonItem>
                      <IonLabel className="ion-text-wrap"><p>{t('assistant.noPosts')}</p></IonLabel>
                    </IonItem>
                  ) : (
                    asArray(outcome.posts).map((post, index) => (
                      <IonItem key={post.id}>
                        <IonLabel className="ion-text-wrap">
                          <div className="dash-row">
                            <strong>#{index + 1}</strong>
                            {asArray(post.platformIds).map((id) => <PlatformChip key={id} platform={id} />)}
                          </div>
                          <h4>{post.title ?? t('assistant.untitled')}</h4>
                          <IonNote>
                            {t('assistant.topMeta', {
                              metric: String(outcome.metric ?? '').replace(/_/g, ' '),
                              value: numberFormat.format(asNumber(post.metricValue)),
                              impressions: numberFormat.format(asNumber(post.impressions)),
                            })}
                          </IonNote>
                        </IonLabel>
                      </IonItem>
                    ))
                  )}
                </IonList>
              )}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}


